import { createTheme } from '@mui/material/styles';

/*
 * ─── HOW TO ADD A NEW THEME ───────────────────────────────────────────────
 *  1. Add a new entry to the THEMES array below.
 *  2. Set a unique `key` (used as data-theme attribute on <body>).
 *  3. Fill in `label`, `icon`, `colors`, and `mui` fields.
 *  4. Add a matching [data-theme="your-key"] block in index.css.
 *  That's it — the switcher UI picks it up automatically.
 * ──────────────────────────────────────────────────────────────────────────
 */

const THEMES = [
  {
    key:   'ocean-blue',
    label: 'Ocean Blue',
    icon:  '🌊',
    colors: {
      primary:      '#1565C0',
      primaryDark:  '#0D47A1',
      primaryLight: '#42A5F5',
      accent:       '#1E88E5',
      bg:           '#FFFFFF',
      bgSection:    '#F0F6FF',
      bgCard:       '#FFFFFF',
      topbarBg:     '#0D47A1',
      topbarText:   '#FFFFFF',
      navbarBg:     '#FFFFFF',
      navbarText:   '#1A237E',
      heading:      '#1A237E',
      text:         '#546E7A',
      border:       '#BBDEFB',
      footerBg:     '#0D47A1',
      footerText:   '#FFFFFF',
    },
    mui: createTheme({
      palette: {
        mode: 'light',
        primary:    { main: '#1565C0', dark: '#0D47A1', light: '#42A5F5' },
        secondary:  { main: '#1E88E5' },
        background: { default: '#FFFFFF', paper: '#F0F6FF' },
        text:       { primary: '#1A237E', secondary: '#546E7A' },
      },
      typography: { fontFamily: "'Segoe UI', Roboto, 'Helvetica Neue', sans-serif" },
      components: {
        MuiButton: { styleOverrides: { containedPrimary: { backgroundColor: '#1565C0', '&:hover': { backgroundColor: '#0D47A1' } } } },
        MuiCard:   { styleOverrides: { root: { border: '1px solid #BBDEFB', boxShadow: '0 2px 12px rgba(21,101,192,0.10)' } } },
      },
    }),
  },

  {
    key:   'midnight-navy',
    label: 'Midnight Navy',
    icon:  '🌙',
    colors: {
      primary:      '#42A5F5',
      primaryDark:  '#1E88E5',
      primaryLight: '#90CAF9',
      accent:       '#64B5F6',
      bg:           '#0A1628',
      bgSection:    '#0D1F3C',
      bgCard:       '#0D1F3C',
      topbarBg:     '#050E1A',
      topbarText:   '#90CAF9',
      navbarBg:     '#0A1628',
      navbarText:   '#E3F2FD',
      heading:      '#E3F2FD',
      text:         '#B0BEC5',
      border:       '#1E3A5F',
      footerBg:     '#050E1A',
      footerText:   '#E3F2FD',
    },
    mui: createTheme({
      palette: {
        mode: 'dark',
        primary:    { main: '#42A5F5', dark: '#1E88E5', light: '#90CAF9' },
        secondary:  { main: '#1E88E5' },
        background: { default: '#0A1628', paper: '#0D1F3C' },
        text:       { primary: '#E3F2FD', secondary: '#B0BEC5' },
      },
      typography: { fontFamily: "'Segoe UI', Roboto, 'Helvetica Neue', sans-serif" },
      components: {
        MuiButton: { styleOverrides: { containedPrimary: { backgroundColor: '#1E88E5', '&:hover': { backgroundColor: '#42A5F5' } } } },
        MuiCard:   { styleOverrides: { root: { border: '1px solid #1E3A5F', boxShadow: '0 2px 12px rgba(0,0,0,0.40)' } } },
      },
    }),
  },

  {
    key:   'slate-pro',
    label: 'Slate Pro',
    icon:  '💎',
    colors: {
      primary:      '#2563EB',
      primaryDark:  '#1D4ED8',
      primaryLight: '#60A5FA',
      accent:       '#3B82F6',
      bg:           '#FFFFFF',
      bgSection:    '#F8FAFC',
      bgCard:       '#FFFFFF',
      topbarBg:     '#0F172A',
      topbarText:   '#CBD5E1',
      navbarBg:     '#FFFFFF',
      navbarText:   '#1E293B',
      heading:      '#0F172A',
      text:         '#475569',
      border:       '#E2E8F0',
      footerBg:     '#0F172A',
      footerText:   '#F1F5F9',
    },
    mui: createTheme({
      palette: {
        mode: 'light',
        primary:    { main: '#2563EB', dark: '#1D4ED8', light: '#60A5FA' },
        secondary:  { main: '#64748B' },
        background: { default: '#FFFFFF', paper: '#F8FAFC' },
        text:       { primary: '#0F172A', secondary: '#475569' },
      },
      typography: {
        fontFamily: "'Inter', 'Segoe UI', Roboto, sans-serif",
        fontWeightMedium: 500,
        fontWeightBold: 700,
      },
      shape: { borderRadius: 12 },
      components: {
        MuiButton: {
          styleOverrides: {
            containedPrimary: {
              background: 'linear-gradient(135deg, #2563EB 0%, #1D4ED8 100%)',
              boxShadow: '0 4px 14px rgba(37,99,235,0.35)',
              '&:hover': {
                background: 'linear-gradient(135deg, #1D4ED8 0%, #1E40AF 100%)',
                boxShadow: '0 6px 20px rgba(37,99,235,0.45)',
                transform: 'translateY(-1px)',
              },
            },
          },
        },
        MuiCard: {
          styleOverrides: {
            root: {
              borderRadius: '16px',
              border: '1px solid #E2E8F0',
              boxShadow: '0 1px 3px rgba(0,0,0,0.04), 0 8px 24px rgba(37,99,235,0.07)',
              transition: 'box-shadow 0.25s ease, transform 0.25s ease',
              '&:hover': {
                boxShadow: '0 4px 12px rgba(0,0,0,0.06), 0 16px 40px rgba(37,99,235,0.12)',
                transform: 'translateY(-2px)',
              },
            },
          },
        },
        MuiTextField: {
          styleOverrides: {
            root: { '& .MuiOutlinedInput-root': { borderRadius: '10px' } },
          },
        },
      },
    }),
  },

  // ── Add more themes below this line ──────────────────────────────────────
];

export default THEMES;

export const getTheme      = (key) => THEMES.find(t => t.key === key) || THEMES[0];
export const getThemeKeys  = ()    => THEMES.map(t => t.key);
export const DEFAULT_THEME = THEMES[0].key;
